import type { MouseEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import IconButton from '__components/Kizen/IconButton';
import { useTooltip } from '__components/Kizen/Tooltip';
import { CALENDAR_VIEW_OPTIONS } from 'components/Calendar/constants';
import { useTranslation } from 'react-i18next';

const AddButton = styled.div<{ view: string }>`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-medium);
  box-shadow: 0 1px 3px
    color-mix(in srgb, var(--color-background-black) 5%, transparent);
  background-color: var(--color-background-white);
  &:hover {
    border-color: var(--color-button-secondary-hover);
    &&& > button > i {
      color: var(--color-button-secondary-hover);
    }
  }
  cursor: pointer;

  ${({ view }) =>
    view === CALENDAR_VIEW_OPTIONS.day
      ? css`
          width: 25px;
          height: 30px;
          left: -1px;
          top: 50%;
          transform: translateY(-50%);
          border-radius: 0 15px 15px 0;
          &:hover {
            border-left-color: var(--color-border-medium);
          }
        `
      : css`
          width: 30px;
          height: 25px;
          top: -1px;
          left: 50%;
          transform: translateX(-50%);
          border-radius: 0 0 15px 15px;
          &:hover {
            border-top-color: var(--color-border-medium);
          }
        `}
`;

interface ScheduleActivityButtonProps {
  view: string;
  show: boolean;
  onClick: (e: MouseEvent) => void;
}

export const ScheduleActivityButton = ({
  view,
  show,
  onClick,
}: ScheduleActivityButtonProps) => {
  const { t } = useTranslation();
  const [tooltipProps, tooltip] = useTooltip(
    {
      label: t('Schedule Activity'),
    },
    () => true
  );

  return show ? (
    <AddButton
      view={view}
      {...tooltipProps}
      onClick={onClick}
      data-qa-id="schedule-activity-button"
    >
      {tooltip}
      <IconButton
        sizing="dense"
        color="var(--color-background-dark)"
        popperConfig={{
          modifiers: {
            offset: {
              offset: view === CALENDAR_VIEW_OPTIONS.day ? '0, 6' : '0, 4',
            },
            preventOverflow: { enabled: false },
          },
        }}
      >
        <IconSizing size="16px">
          <Icon icon="plus" />
        </IconSizing>
      </IconButton>
    </AddButton>
  ) : null;
};
